import { CompanySearchInput } from '../../features/company-search-input/company-search-input';
import { DateInput } from '../../forms/date-input';
import { Input } from '../../forms/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../forms/select';
import type { FieldType, ValueType } from './InformationCard.type';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

type InlineInputProps = {
  type: FieldType;
  value: ValueType | undefined;
  onChange: (value: ValueType | undefined) => void;
  className?: string;
  fieldKey?: string;
  options?: string[];
};

export const InlineInput = ({
  type,
  value,
  onChange,
  className,
  fieldKey,
  options,
}: InlineInputProps) => {
  const { t } = useTranslation();

  if (type === 'select') {
    return (
      <Select value={typeof value === 'string' ? value : undefined} onValueChange={onChange}>
        <SelectTrigger className={className}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(options ?? []).map(option => (
            <SelectItem key={option} value={option}>
              {t([`fields.${fieldKey}.options.${option}`, `options.${option}`, option])}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (type === 'date') {
    return (
      <DateInput
        value={typeof value === 'string' ? value : undefined}
        onChange={onChange}
        className={className}
      />
    );
  }

  if (type === 'number') {
    return (
      <Input
        type="number"
        value={typeof value === 'number' ? value : undefined}
        onChange={e => {
          onChange(Number(e.target.value));
        }}
        className={className}
      />
    );
  }

  if (type === 'siret') {
    return (
      <CompanySearchInput
        onCompanySelect={company => {
          onChange(company.siret);
        }}
        className={className}
      />
    );
  }

  if (type === 'boolean') {
    // Labels résolus via i18n avec fallback sur "Oui"/"Non".
    // Permet à un tenant d'override les labels par fieldKey
    // (ex: EGA → "Professionnel"/"Particulier" pour isCustomerLegalPerson).
    const trueLabel = fieldKey
      ? t([`fields.${fieldKey}.options.true`, 'options.true'])
      : t('options.true');
    const falseLabel = fieldKey
      ? t([`fields.${fieldKey}.options.false`, 'options.false'])
      : t('options.false');

    return (
      <Select
        value={typeof value === 'boolean' ? value.toString() : undefined}
        onValueChange={value => {
          onChange(value === 'true');
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Sélectionnez un type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={'true'}>{trueLabel}</SelectItem>
          <SelectItem value={'false'}>{falseLabel}</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      type={type === 'postcode' ? 'text' : type}
      value={typeof value !== 'boolean' ? value : undefined}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      className={className}
    />
  );
};
